import React, { useState } from 'react';
import { ArrowRightIcon, CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { inquirySubjects } from '../data/profile';
type Status = 'idle' | 'submitting' | 'success';
interface FormValues {
  name: string;
  company: string;
  email: string;
  subject: string;
  message: string;
}
const initialValues: FormValues = {
  name: '',
  company: '',
  email: '',
  subject: inquirySubjects[0],
  message: ''
};
const fieldClasses = 'w-full border border-line bg-white px-4 py-3.5 text-[15px] text-navy placeholder:text-charcoal-muted/70 transition-colors duration-150 ease-premium focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy';
const labelClasses = 'mb-2 block text-[12px] font-semibold uppercase tracking-label text-charcoal';
export function ContactForm() {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [status, setStatus] = useState<Status>('idle');
  const handleChange = (field: keyof FormValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValues(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    window.setTimeout(() => {
      setStatus('success');
      setValues(initialValues);
    }, 900);
  };
  if (status === 'success') {
    return <div role="status" className="flex h-full flex-col items-start justify-center border border-line bg-white p-10 lg:p-12" data-id="element-168" data-mp="3bl5jocudq9ky">
        <CheckCircle2Icon className="h-8 w-8 text-metal" aria-hidden="true" data-id="element-169" data-mp="3gixb75r43tom" />
        <h3 className="mt-5 font-serif text-2xl font-semibold text-navy" data-id="element-170" data-mp="3kn51h7d7xhoy">Message received</h3>
        <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-charcoal-light" data-id="element-171" data-mp="vv14kcgo7061">
          Thank you for reaching out. You can expect a direct response within one business day.
        </p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-7 text-[13px] font-semibold uppercase tracking-label text-navy underline decoration-metal decoration-2 underline-offset-4 transition-colors duration-150 ease-premium hover:text-metal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2" data-id="element-172" data-mp="2wvpljo5xhfnb">
          Send another message
        </button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="border border-line bg-white p-8 sm:p-10 lg:p-12" data-id="element-173" data-mp="2976bp478cut4">
      <h3 className="font-serif text-2xl font-semibold tracking-tight text-navy" data-id="element-174" data-mp="2my1d5vfeg9mo">
        Board & advisory inquiries
      </h3>
      <p className="mt-2 text-[14px] text-charcoal-muted" data-id="element-175" data-mp="2i7cp6q2xhusa">
        All inquiries are reviewed personally and answered directly.
      </p>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2" data-id="element-176" data-mp="1ug74nxm1mebn">
        <div data-id="element-177" data-mp="1phvuflru61sw">
          <label htmlFor="contact-name" className={labelClasses} data-id="element-178" data-mp="1pie17cc4jyf2">
            Name
          </label>
          <input id="contact-name" name="name" type="text" required autoComplete="name" value={values.name} onChange={handleChange('name')} placeholder="Full name" className={fieldClasses} data-id="element-179" data-mp="4zbpyiw8qsru" />
        </div>
        <div data-id="element-180" data-mp="x6sta6l3e0g">
          <label htmlFor="contact-company" className={labelClasses} data-id="element-181" data-mp="oovtbdtk78sh">
            Company / Organization
          </label>
          <input id="contact-company" name="company" type="text" autoComplete="organization" value={values.company} onChange={handleChange('company')} placeholder="Organization name" className={fieldClasses} data-id="element-182" data-mp="sqz4s2g8addr" />
        </div>
        <div data-id="element-183" data-mp="2zg5crbo7cz5v">
          <label htmlFor="contact-email" className={labelClasses} data-id="element-184" data-mp="2zflyc4xrgswl">
            Email
          </label>
          <input id="contact-email" name="email" type="email" required autoComplete="email" value={values.email} onChange={handleChange('email')} placeholder="name@company.com" className={fieldClasses} data-id="element-185" data-mp="34hb71dxiguhi" />
        </div>
        <div data-id="element-186" data-mp="35yicqzbtr7jh">
          <label htmlFor="contact-subject" className={labelClasses} data-id="element-187" data-mp="35z1htfgdm1g3">
            Subject
          </label>
          <select id="contact-subject" name="subject" value={values.subject} onChange={handleChange('subject')} className={`${fieldClasses} appearance-none bg-[length:16px] pr-10`} data-id="element-188" data-mp="6jejv0wlog52">
            {inquirySubjects.map(subject => <option key={subject} value={subject} data-id="element-189" data-mp="3onomhwun4ot5">
                {subject}
              </option>)}
          </select>
        </div>
        <div className="md:col-span-2" data-id="element-190" data-mp="2i6b87cvy3u8z">
          <label htmlFor="contact-message" className={labelClasses} data-id="element-191" data-mp="2d9bcnvwq29w4">
            Message
          </label>
          <textarea id="contact-message" name="message" required rows={5} value={values.message} onChange={handleChange('message')} placeholder="Share the opportunity, timeline, and what you are looking for." className={`${fieldClasses} resize-none`} data-id="element-192" data-mp="236p9jtkd1elb" />
        </div>
      </div>

      <button type="submit" disabled={status === 'submitting'} className="group mt-8 inline-flex items-center gap-2.5 bg-navy px-7 py-4 text-sm font-semibold text-white transition-colors duration-150 ease-premium hover:bg-navy-deep disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2" data-id="element-193" data-mp="2lwngu7gc33nw">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-194" data-mp="11in6ro1f9oze" />
            Sending
          </> : <>
            Send message
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-premium group-hover:translate-x-1" aria-hidden="true" data-id="element-195" data-mp="yf7kliik6h2y" />
          </>}
      </button>
    </form>;
}